/**
 * Helpers shared by the import-only CSV configs that post to the `/imports/*`
 * endpoints. Those request schemas reject unknown properties and treat an absent
 * key differently from a null one, so blank cells have to be dropped from the
 * payload rather than sent as empty strings.
 */

export function trimmedOrUndefined(value: string | undefined): string | undefined {
  return value?.trim() || undefined;
}

export function numberOrUndefined(value: string | undefined): number | undefined {
  const text = trimmedOrUndefined(value);
  return text === undefined ? undefined : Number(text);
}

/**
 * Drops every key whose value is `undefined`, and returns `undefined` when
 * nothing is left, so an object or array can be omitted from the payload
 * entirely instead of being sent empty.
 */
export function withoutEmptyValues<T extends object>(candidate: T): T | undefined {
  const populatedEntries = Object.entries(candidate).filter(
    ([, value]) => value !== undefined,
  );

  return populatedEntries.length > 0
    ? (Object.fromEntries(populatedEntries) as T)
    : undefined;
}

// Stated explicitly rather than inferred: withoutEmptyValues types its keys as required
// with undefined values, which is not assignable to the spec's optional-key site shape.
type SiteEntry<TWaypoint> = {
  waypoint: TWaypoint;
  name?: string;
  external_id?: string;
  full_address?: string;
};

/**
 * One site entry for an import payload, or undefined when the row named no site at all.
 * Generic over the waypoint type so each entity's own spec enum flows through.
 */
export function buildImportSite<TWaypoint>(
  waypoint: TWaypoint,
  name: string | undefined,
  externalId: string | undefined,
  fullAddress: string | undefined,
): SiteEntry<TWaypoint> | undefined {
  const site = withoutEmptyValues({
    name: trimmedOrUndefined(name),
    external_id: trimmedOrUndefined(externalId),
    full_address: trimmedOrUndefined(fullAddress),
  });

  return site ? { waypoint, ...site } : undefined;
}
